// Lobby system for Wordsheet multiplayer
// Handles game creation, joining, and pre-game player management

import { db } from './firebase-config.js';
import { TileBag, generateSupplementalTiles, resolveBagSelection } from './tiles.js';
import { getRandomLayoutName, DEFAULT_BOARD_SIZE } from './board.js';
import { collection, doc, getDoc, setDoc, updateDoc, deleteDoc, onSnapshot, arrayUnion, serverTimestamp, runTransaction } from 'firebase/firestore';
import { createPlayer, spinWheelOfFortune } from '@wordsheet/shared';
import { flags } from './flags.js';

export class Lobby {
    constructor(playerId, nickname) {
        this.playerId = playerId;
        this.nickname = nickname;
        this.gameCode = null;
        this.gameRef = null;
        this.unsubscribe = null;
        this.isHost = false;
        this.lastStatus = null;

        this.playersCallback = null;
        this.gameStartCallback = null;
    }

    // Generate a 6-character alphanumeric game code
    generateGameCode() {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Avoid confusing chars like O/0, I/1
        let code = '';
        for (let i = 0; i < 6; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
    }

    // Create a new game
    async createGame() {
        console.log('Lobby.createGame() called');
        this.isHost = true;

        const maxAttempts = 5;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const gameCode = this.generateGameCode();
            console.log('Generated game code:', gameCode, '- checking if exists...');
            const gameRef = doc(db, 'games', gameCode);
            console.log('Calling getDoc...');
            const existing = await getDoc(gameRef);
            console.log('getDoc returned, exists:', existing.exists());
            if (existing.exists()) {
                continue;
            }

            const gameData = {
                gameCode: gameCode,
                status: 'waiting',
                hostPlayerId: this.playerId,
                createdAt: serverTimestamp(),
                currentTurnIndex: 0,
                isFirstMove: true,
                boardTiles: [],
                tileBag: [],
                layoutName: getRandomLayoutName(),
                boardSize: DEFAULT_BOARD_SIZE,
                version: 1,
                // playerIds array for Firestore rules membership checks
                playerIds: [this.playerId],
                players: [createPlayer(this.playerId, this.nickname)],
                moves: []
            };

            try {
                await setDoc(gameRef, gameData);
                this.gameCode = gameCode;
                this.gameRef = gameRef;
                this.subscribeToGame();
                return this.gameCode;
            } catch (error) {
                console.error('Error creating game:', error);
                throw error;
            }
        }

        throw new Error('Failed to generate unique game code');
    }

    // Join an existing game
    async joinGame(gameCode) {
        this.gameCode = gameCode.toUpperCase();
        this.gameRef = doc(db, 'games', this.gameCode);

        try {
            const docSnap = await getDoc(this.gameRef);

            if (!docSnap.exists()) {
                throw new Error('GAME_NOT_FOUND');
            }

            const data = docSnap.data();

            if (data.status === 'active') {
                // Check if player is already in the game (rejoin)
                const existingPlayer = data.players.find(p => p.id === this.playerId);
                if (existingPlayer) {
                    // Player is rejoining - update their active status
                    const playerIndex = data.players.findIndex(p => p.id === this.playerId);
                    const updatedPlayers = [...data.players];
                    if (existingPlayer.isFinished) {
                        if (updatedPlayers[playerIndex].isActive) {
                            updatedPlayers[playerIndex] = {
                                ...updatedPlayers[playerIndex],
                                isActive: false
                            };
                            await updateDoc(this.gameRef, { players: updatedPlayers });
                        }
                    } else {
                        updatedPlayers[playerIndex] = {
                            ...updatedPlayers[playerIndex],
                            isActive: true,
                            lastActiveAt: Date.now()
                        };
                        await updateDoc(this.gameRef, { players: updatedPlayers });
                    }
                    this.subscribeToGame();
                    return { gameCode: this.gameCode, status: data.status };
                }

                // Late-join: new player joining an active game
                if (data.players.length >= 5) {
                    throw new Error('GAME_FULL');
                }

                // Use transaction to atomically add supplemental tiles, deal, and add player
                await runTransaction(db, async (transaction) => {
                    const freshDoc = await transaction.get(this.gameRef);
                    if (!freshDoc.exists()) {
                        throw new Error('GAME_NOT_FOUND');
                    }
                    const freshData = freshDoc.data();

                    // Re-check conditions inside transaction
                    if (freshData.status === 'ended') {
                        throw new Error('GAME_ENDED');
                    }
                    if (freshData.players.length >= 5) {
                        throw new Error('GAME_FULL');
                    }
                    if (freshData.players.find(p => p.id === this.playerId)) {
                        // Player was added by another transaction, just return
                        return;
                    }

                    // Generate 7 supplemental tiles and add to bag
                    const supplemental = generateSupplementalTiles(7, freshData.tileBagVariant);
                    const tileBag = [...(freshData.tileBag || []), ...supplemental];
                    // Shuffle bag
                    for (let i = tileBag.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [tileBag[i], tileBag[j]] = [tileBag[j], tileBag[i]];
                    }

                    // Deal 7 tiles from the bag
                    const rack = [];
                    for (let i = 0; i < 7 && tileBag.length > 0; i++) {
                        rack.push(tileBag.pop());
                    }

                    // Late-join bonus: spin the wheel of fortune
                    const lateJoinBonus = spinWheelOfFortune();
                    const newPlayer = createPlayer(this.playerId, this.nickname, {
                        rack,
                        score: lateJoinBonus,
                        lateJoinBonus
                    });

                    // Add bonus move record
                    const bonusMove = {
                        moveId: `bonus_${this.playerId}_${Date.now()}`,
                        playerId: this.playerId,
                        nickname: this.nickname,
                        timestamp: Date.now(),
                        type: 'bonus',
                        score: lateJoinBonus,
                        words: []
                    };
                    const moves = [...(freshData.moves || []), bonusMove];

                    transaction.update(this.gameRef, {
                        players: [...freshData.players, newPlayer],
                        playerIds: [...(freshData.playerIds || []), this.playerId],
                        tileBag: tileBag,
                        moves: moves
                    });
                });

                this.subscribeToGame();
                // Read back the player to get lateJoinBonus (it was set in the transaction)
                const freshDoc = await getDoc(this.gameRef);
                const freshData = freshDoc.data();
                const joinedPlayer = freshData.players.find(p => p.id === this.playerId);
                return {
                    gameCode: this.gameCode,
                    status: data.status,
                    lateJoinBonus: joinedPlayer?.lateJoinBonus
                };
            }

            if (data.status === 'ended') {
                throw new Error('GAME_ENDED');
            }

            if (data.players.length >= 5) {
                throw new Error('GAME_FULL');
            }

            // Check if player is already in the lobby
            const existingPlayer = data.players.find(p => p.id === this.playerId);
            if (!existingPlayer) {
                // Add player to the game
                const newPlayer = createPlayer(this.playerId, this.nickname);
                // Add both the player object and the playerId for rules membership checks
                await updateDoc(this.gameRef, {
                    players: arrayUnion(newPlayer),
                    playerIds: arrayUnion(this.playerId)
                });
            }

            this.isHost = data.hostPlayerId === this.playerId;
            this.subscribeToGame();
            return { gameCode: this.gameCode, status: data.status };
        } catch (error) {
            console.error('Error joining game:', error);
            throw error;
        }
    }

    // Subscribe to game document changes
    subscribeToGame() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }

        this.unsubscribe = onSnapshot(this.gameRef, (docSnap) => {
            if (!docSnap.exists()) {
                console.error('Game document deleted');
                return;
            }

            const data = docSnap.data();
            this.lastStatus = data.status;

            // Update players list
            if (this.playersCallback) {
                this.playersCallback(data.players);
            }

            // Check if game has started (only trigger once)
            if (data.status === 'active' && this.gameStartCallback) {
                const callback = this.gameStartCallback;
                this.gameStartCallback = null; // Clear to prevent multiple calls
                callback();
            }
        }, (error) => {
            console.error('Error listening to game:', error);
        });
    }

    // Start the game (host only)
    async startGame() {
        if (!this.isHost) {
            console.error('Only the host can start the game');
            return;
        }

        try {
            const docSnap = await getDoc(this.gameRef);
            const data = docSnap.data();

            // Create base tile bag based on flag + supplemental tiles for 4+ players
            const playerCount = data.players.length;
            const bagChoice = resolveBagSelection(flags.get('tileBag'));
            const newTileBag = new TileBag(bagChoice.key);
            const tileBag = newTileBag.tiles.map(tile => ({
                letter: tile.letter,
                points: tile.points,
                isBlank: tile.isBlank,
                id: tile.id
            }));
            if (playerCount >= 4) {
                const supplemental = generateSupplementalTiles((playerCount - 3) * 7, bagChoice.key);
                tileBag.push(...supplemental);
                // Re-shuffle combined bag
                for (let i = tileBag.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [tileBag[i], tileBag[j]] = [tileBag[j], tileBag[i]];
                }
            }
            const players = data.players.map(player => {
                const rack = [];
                for (let i = 0; i < 7 && tileBag.length > 0; i++) {
                    rack.push(tileBag.pop());
                }
                return {
                    ...player,
                    rack: rack,
                    lastActiveAt: Date.now()
                };
            });

            await updateDoc(this.gameRef, {
                status: 'active',
                players: players,
                tileBag: tileBag,
                tileBagSize: tileBag.length + (playerCount * 7),
                tileBagVariant: bagChoice.key,
                tileBagName: bagChoice.name,
                startedAt: Date.now(),
                gameFlags: flags.all()
            });
        } catch (error) {
            console.error('Error starting game:', error);
            throw error;
        }
    }

    // Leave the lobby
    async leave() {
        if (!this.gameRef) return;

        try {
            const docSnap = await getDoc(this.gameRef);
            if (!docSnap.exists()) return;

            const data = docSnap.data();

            // If game hasn't started, remove player from list
            if (data.status === 'waiting') {
                const updatedPlayers = data.players.filter(p => p.id !== this.playerId);
                const updatedPlayerIds = (data.playerIds || []).filter(id => id !== this.playerId);

                if (updatedPlayers.length === 0) {
                    // Delete the game if no players left
                    await deleteDoc(this.gameRef);
                } else {
                    // If host is leaving, transfer to next player
                    const updateData = {
                        players: updatedPlayers,
                        playerIds: updatedPlayerIds
                    };
                    if (data.hostPlayerId === this.playerId) {
                        updateData.hostPlayerId = updatedPlayers[0].id;
                    }
                    await updateDoc(this.gameRef, updateData);
                }
            }
        } catch (error) {
            console.error('Error leaving lobby:', error);
        } finally {
            this.cleanup();
        }
    }

    // Register callback for player list updates
    onPlayersUpdate(callback) {
        this.playersCallback = callback;
    }

    // Register callback for game start
    onGameStart(callback) {
        if (this.lastStatus === 'active') {
            callback();
            return;
        }
        this.gameStartCallback = callback;
    }

    // Cleanup subscriptions
    cleanup() {
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
        this.gameRef = null;
        this.playersCallback = null;
        this.gameStartCallback = null;
        this.lastStatus = null;
    }
}
